import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { getEmail, getPhoneDisplay, getPhoneHref } from '../data/contactDetails';

function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3.5 6.5l8.5 6.5 8.5-6.5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.6 3.5h2.6l1.4 4-2 1.3a11 11 0 005.6 5.6l1.3-2 4 1.4v2.6a2 2 0 01-2.2 2A16.5 16.5 0 014.6 5.7a2 2 0 012-2.2z"
        stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"
      />
    </svg>
  );
}

// Where "Send Email" can go. Webmail options open a pre-addressed compose
// window in a new browser tab; "app" hands off to the device's mail app.
const EMAIL_OPTIONS = [
  { id: 'gmail',   label: 'Gmail',   url: to => `https://mail.google.com/mail/?view=cm&fs=1&to=${to}` },
  { id: 'outlook', label: 'Outlook', url: to => `https://outlook.live.com/mail/0/deeplink/compose?to=${to}` },
  { id: 'yahoo',   label: 'Yahoo Mail', url: to => `https://compose.mail.yahoo.com/?to=${to}` },
  { id: 'app',     label: 'Email app on this device' },
  { id: 'copy',    label: 'Copy address' },
];

/**
 * "Send Email" / "Call Now". The address and number stay hidden until
 * clicked. Send Email then asks how to send (Gmail / Outlook / Yahoo in the
 * browser, the device's mail app, or copy); Call Now reveals the number and
 * opens tel: (desktop users without a phone app can still read it).
 */
export default function ContactActions({ compact = false, onDone }) {
  const { colors } = useTheme();
  const [emailOpen, setEmailOpen] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(null);

  const sendEmail = async (option) => {
    const address = getEmail();
    if (option.url) {
      window.open(option.url(encodeURIComponent(address)), '_blank', 'noopener,noreferrer');
    } else if (option.id === 'app') {
      window.location.href = `mailto:${address}`;
    } else if (option.id === 'copy') {
      try {
        await navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Clipboard blocked — the address is shown above for manual copying
      }
      return;
    }
    onDone?.();
  };

  const callNow = () => {
    setPhoneRevealed(true);
    window.location.href = `tel:${getPhoneHref()}`;
  };

  const mainButton = (id, isActive) => ({
    display: 'flex', alignItems: 'center', gap: '0.9rem', width: '100%',
    padding: compact ? '0.75rem 0.9rem' : '1rem 1.1rem',
    textAlign: 'left', cursor: 'pointer',
    background: isActive ? colors.accentBg : colors.surface,
    color: isActive ? colors.accentText : colors.text,
    border: `1px solid ${isActive ? colors.accentBg : colors.border}`,
    borderRadius: compact ? '8px' : 0,
    transition: 'background 0.2s, color 0.2s, border-color 0.2s',
    fontFamily: "'Inter', sans-serif",
  });
  const iconWrap = isActive => ({ display: 'flex', flexShrink: 0, color: isActive ? colors.accentText : colors.accent });
  const labelText = {
    display: 'block', fontWeight: 700, fontSize: compact ? '0.82rem' : '0.88rem',
    letterSpacing: '1px', textTransform: 'uppercase',
  };
  const detailText = { display: 'block', marginTop: '3px', fontSize: '0.82rem', opacity: 0.85, overflowWrap: 'anywhere' };

  const emailActive = hovered === 'email' || emailOpen;
  const phoneActive = hovered === 'phone';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: compact ? '0.6rem' : '0.8rem', alignItems: 'start',
      }}
    >
      <div>
        <button
          type="button"
          onClick={() => setEmailOpen(open => !open)}
          onMouseEnter={() => setHovered('email')}
          onMouseLeave={() => setHovered(null)}
          aria-expanded={emailOpen}
          style={mainButton('email', emailActive)}
        >
          <span style={iconWrap(emailActive)}><MailIcon /></span>
          <span style={{ minWidth: 0 }}>
            <span style={labelText}>Send Email</span>
            {emailOpen && <span style={{ ...detailText, userSelect: 'all' }}>{getEmail()}</span>}
          </span>
        </button>

        {emailOpen && (
          <div
            role="group"
            aria-label="Choose how to send your email"
            style={{
              marginTop: '0.4rem', padding: '0.35rem',
              border: `1px solid ${colors.border}`, borderRadius: compact ? '8px' : 0,
              background: colors.bg2,
            }}
          >
            <div style={{ fontSize: '0.72rem', color: colors.muted, padding: '0.35rem 0.55rem 0.25rem' }}>
              Send with…
            </div>
            {EMAIL_OPTIONS.map(option => {
              const isHovered = hovered === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => sendEmail(option)}
                  onMouseEnter={() => setHovered(option.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    width: '100%', padding: '0.5rem 0.55rem', border: 'none', borderRadius: '6px',
                    background: isHovered ? colors.surface2 : 'transparent', color: colors.text,
                    fontSize: '0.84rem', cursor: 'pointer', textAlign: 'left',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <span>{option.id === 'copy' && copied ? 'Copied ✓' : option.label}</span>
                  {option.url && <span aria-hidden="true" style={{ color: colors.muted, fontSize: '0.8rem' }}>↗</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={callNow}
        onMouseEnter={() => setHovered('phone')}
        onMouseLeave={() => setHovered(null)}
        style={mainButton('phone', phoneActive)}
      >
        <span style={iconWrap(phoneActive)}><PhoneIcon /></span>
        <span style={{ minWidth: 0 }}>
          <span style={labelText}>Call Now</span>
          {phoneRevealed && <span style={detailText}>{getPhoneDisplay()}</span>}
        </span>
      </button>
    </div>
  );
}
